/**
 * Edit + actions for a customer record.
 *
 * Editing a customer's own details is reachable from more than one screen
 * (the customer record, and the Customer card on a registration), and both
 * should offer it the same way: a visible Edit, not an action buried in a
 * "..." menu. This is the one control both use, so the affordance cannot
 * drift between them.
 *
 * Edit externally triggers the tenant's configured ``edit_customer`` action
 * rather than reimplementing the form, so the dialog, its reason gate,
 * composite-field handling, and permission gate all stay in
 * ``EntityActionMenu``. The menu keeps whatever other customer actions the
 * tenant has configured and drops the duplicate edit entry; when edit is the
 * only action, the trigger is hidden so no empty menu is rendered.
 */

import { useState, type ReactNode } from 'react'
import { Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import { EntityActionMenu } from '@/admin/components/shared/entity-action-menu'
import { useEntityTypeByKey } from '@/admin/hooks/use-entities'
import {
  useEvaluatedEntityActions,
  type EntityAction,
} from '@/admin/hooks/use-entity-actions'

/** The action that owns editing a customer's own details. */
export const EDIT_CUSTOMER_ACTION_KEY = 'edit_customer'

export function CustomerActionControls({
  consumerUuid,
  holdingUuid,
  onActionComplete,
  onNavigate,
  triggerClassName,
  triggerLabel,
  triggerTestId,
  editButtonTestId = 'customer-edit-btn',
}: {
  consumerUuid: string
  /** The registration the card is on, so an address edit corrects its copy. */
  holdingUuid?: string
  onActionComplete?: () => void
  onNavigate?: (action: EntityAction) => void
  triggerClassName?: string
  triggerLabel?: string
  triggerTestId?: string
  editButtonTestId?: string
}): ReactNode {
  const { t } = useTranslation()
  const [triggeredActionKey, setTriggeredActionKey] = useState<string | null>(null)

  const { data: entity } = useEntityTypeByKey('consumer')
  const { data: evaluated } = useEvaluatedEntityActions(
    entity?.uuid,
    consumerUuid,
    holdingUuid,
  )

  // The backend already filtered these by permission, so an action's presence
  // is the gate - no separate `can()` check that could drift from it.
  const actions = evaluated?.actions ?? []
  const canEdit = actions.some((action) => action.key === EDIT_CUSTOMER_ACTION_KEY)
  const hasOtherActions = actions.some(
    (action) => action.key !== EDIT_CUSTOMER_ACTION_KEY,
  )

  if (!canEdit && !hasOtherActions) return null

  return (
    <>
      {canEdit ? (
        <Button
          variant="ghost"
          size="xs"
          data-testid={editButtonTestId}
          onClick={() => setTriggeredActionKey(EDIT_CUSTOMER_ACTION_KEY)}
        >
          <Pencil className="mr-1 h-3 w-3" />
          {t('common.edit', { defaultValue: 'Edit' })}
        </Button>
      ) : null}
      <EntityActionMenu
        entityTypeKey="consumer"
        resourceUuid={consumerUuid}
        holdingUuid={holdingUuid}
        onActionComplete={onActionComplete}
        onNavigate={onNavigate}
        triggerLabel={triggerLabel}
        triggerTestId={triggerTestId}
        triggerClassName={triggerClassName}
        contentClassName="min-w-[210px]"
        excludeActionKeys={[EDIT_CUSTOMER_ACTION_KEY]}
        hideTrigger={!hasOtherActions}
        externalTriggerActionKey={triggeredActionKey}
        onExternalTriggerReset={() => setTriggeredActionKey(null)}
      />
    </>
  )
}
